/** A generative AI model wrote or edited portions of this file with the supervision of a human developer and careful human review. */

import { setContext, getContext } from "svelte";
import { getAbsoluteUrl } from "$utils/urls.js";
import { parseDate, formatQuarter, formatYear, parseYear } from "$utils/dates.js";
import { getName } from "$utils/geoNames.js";
import { DEFAULT_PLAN_TYPE } from "$utils/consts.js";
import metadata from "$data/metadata.json";
import Papa from "papaparse";

/** @typedef {import("$utils/types").DataObject} DataObject */

export class ToolData {
  meta = metadata;

  /** @type {DataObject[] | undefined} */
  chartRawData = $state();
  /** @type {DataObject[] | undefined} */
  mapRawData = $state();

  /** @type {Map<string, DataObject[]>} */
  #chartCache = new Map();
  /** @type {Map<string, DataObject[]>} */
  #mapCache = new Map();

  /** @type {string | null} */
  activeGeo = $state("00");
  activeTime = $state("2025");
  activeMetric = $state("numberofrx");
  activeFormat = $derived.by(() => this.getMetricFormat(this.activeMetric));
  activeMapCategory = $state("all non-NRT & NRT");
  activeUtilization = $state("all");
  activeFrequency = $state("quarterly");
  activeMedType = $state("all");
  highlightCategory = $state(null);
  showSubcategories = $state(false);
  mapDataNeeded = $state(false);
  activeCategories = $derived(
    this.showSubcategories
      ? metadata.medication_groups[1].types_included
      : metadata.medication_groups[0].types_included
  );

  // Derive time/state lists from metadata instead of scanning data
  quarterList = metadata.quarters;
  yearList = metadata.years;
  stateList = metadata.available_plans.map((s) => ({
    value: s.geoid,
    label: getName(s.geoid)
  }));
  timeList = metadata.all_times;

  chartData = $derived.by(() => this.getChartData());
  mapData = $derived.by(() => this.getMapData());
  statePlans = $derived.by(() => this.getStatePlans());
  availablePlans = $derived(this.statePlans[this.activeGeo]);
  ticks = $derived.by(() => this.getTicks());

  /**
   * Parse a CSV response into data objects.
   * @param {string} text
   * @returns {DataObject[]}
   */
  parseCSV(text) {
    const processed = Papa.parse(text, {
      header: true,
      dynamicTyping: false,
      skipEmptyLines: true,
      transform: this.transformValue
    });
    return processed.data;
  }

  /**
   * Fetch chart data for a specific geography.
   * @param {string} geoid
   */
  async fetchChartData(geoid) {
    if (this.#chartCache.has(geoid)) {
      this.chartRawData = this.#chartCache.get(geoid);
      return;
    }
    const res = await fetch(getAbsoluteUrl(`/data/chart/${geoid}.csv`));
    if (!res.ok) {
      throw new Error(`Failed to fetch chart data for geoid ${geoid}`);
    }
    const data = this.parseCSV(await res.text());
    this.#chartCache.set(geoid, data);
    this.chartRawData = data;
  }

  /**
   * Fetch map data for a given frequency (quarterly or annual).
   * @param {"quarterly" | "annual"} frequency
   */
  async fetchMapData(frequency) {
    const key = frequency === "annual" ? "annual" : "quarterly";
    if (this.#mapCache.has(key)) {
      this.mapRawData = this.#mapCache.get(key);
      return;
    }
    const filename = `map_${key}.csv`;
    const res = await fetch(getAbsoluteUrl(`/data/${filename}`));
    if (!res.ok) {
      throw new Error(`Failed to fetch map data: ${filename}`);
    }
    const data = this.parseCSV(await res.text());
    this.#mapCache.set(key, data);
    this.mapRawData = data;
  }

  /**
   * Determine which map frequency is needed based on activeTime.
   * Year-only values (e.g. "2025") use annual; quarter values use quarterly.
   * @returns {"quarterly" | "annual"}
   */
  getMapFrequency() {
    return this.activeTime.includes("Q") ? "quarterly" : "annual";
  }

  transformValue(value, field) {
    if (value === "NA") {
      return null;
    }
    if (field === "date") {
      return parseDate(value);
    }
    if (field === "geoid") {
      return value;
    }
    if (!isNaN(+value)) {
      return +value;
    }
    return value;
  }

  getChartData() {
    if (!this.chartRawData) return [];

    const categories = this.activeCategories;

    const filteredData = this.chartRawData.filter((d) => {
      const utilizationMatch = d.utilizationtype === this.activeUtilization;
      const categoryMatch = categories.includes(d.product_cat);
      const frequencyMatch =
        (this.activeFrequency === "annual" && !d.quarter) ||
        (this.activeFrequency === "quarterly" && d.quarter);
      const brandMatch = this.activeMedType == d.medtype;

      return utilizationMatch && categoryMatch && frequencyMatch && brandMatch;
    });

    let resultsByTime = new Map();
    for (const entry of filteredData) {
      const timeKey = entry.quarter ? formatQuarter(entry.date) : formatYear(entry.date);
      if (resultsByTime.has(timeKey)) {
        const existing = resultsByTime.get(timeKey);
        existing[entry.product_cat] = entry[this.activeMetric];
        resultsByTime.set(timeKey, existing);
      } else {
        const newEntry = { date: entry.date };
        newEntry[entry.product_cat] = entry[this.activeMetric];
        resultsByTime.set(timeKey, newEntry);
      }
      for (const cat of categories) {
        if (!resultsByTime.get(timeKey)[cat]) {
          resultsByTime.get(timeKey)[cat] = 0;
        }
      }
    }
    return Array.from(resultsByTime.values());
  }

  formatTime(d) {
    if (d.quarter) {
      return formatQuarter(d.date);
    }
    return formatYear(d.date);
  }

  getMapData() {
    if (!this.mapRawData) return [];
    return this.mapRawData
      .filter((d) => {
        const timeMatch = this.formatTime(d) == this.activeTime;
        const utilizationMatch = d.utilizationtype === this.activeUtilization;
        const categoryMatch = this.activeMapCategory === d.product_cat;
        const brandMatch = this.activeMedType === d.medtype;

        return timeMatch && utilizationMatch && categoryMatch && brandMatch;
      })
      .map((d) => {
        return {
          map_id: d.geoid,
          value: d[this.activeMetric]
        };
      });
  }

  getMetricFormat(metric) {
    const metricInfo = this.getMetricMeta(metric);
    return metricInfo?.unit_type || "INT";
  }

  getMetricMeta(metric) {
    const metricInfo = this.meta.metrics.find((m) => m.metric === metric);
    return metricInfo;
  }

  getMetricLabel(metric) {
    const metricInfo = this.getMetricMeta(metric);
    return metricInfo?.full_name || metric;
  }

  /**
   * @param {string} cat
   * @returns {string}
   **/
  formatCategory(cat) {
    let catMeta = this.meta.product_cats.find(({ category }) => category === cat);
    if (!catMeta) {
      console.warn(`No metadata found for category: ${cat}`);
      return cat;
    }
    return catMeta.label;
  }

  getMapCategories() {
    return ["all non-NRT & NRT", "all non-NRT", "all NRT"];
  }

  getBrandLabel(brand) {
    let brandMeta = this.meta.brands.find(({ value }) => value === brand);
    return brandMeta.label;
  }

  getStatePlans() {
    let result = {};
    for (const state of this.meta.available_plans) {
      result[state.geoid] = state.available_plans;
    }
    return result;
  }

  getTicks() {
    return this.yearList.map(parseYear);
  }
}

const contextKey = Symbol("ToolData");

export function createToolData() {
  let toolData = new ToolData();
  setContext(contextKey, toolData);
  return toolData;
}

export function useToolData() {
  return getContext(contextKey);
}
